import React, { useMemo, useState } from "react";
import { Archive, EmptyState, Tabs } from "@opendate-app/setlist";
import VenueActionsMenu from "./VenueActionsMenu";
import TeamSearchBar from "../../TeamManager/components/TeamSearchBar";
import SortIcon from "../../shared/Icons/SortIcon";
import PlusIcon from "../../shared/Icons/PlusIcon";
import BuildingIcon from "../../shared/Icons/BuildingIcon";
import SearchIcon from "../../shared/Icons/SearchIcon";
import "../../TeamManager/styles/teamManagerStyles.css";

const AVATAR_COLORS = [
  '#1982c4', '#e63946', '#2a9d8f', '#f9a825', '#7c3aed',
  '#059669', '#d97706', '#db2777', '#6366f1', '#0891b2',
];

const getVenueInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getVenueColor = (name) => {
  const str = name || '';
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const DOT_COLORS = ['blue', 'red', 'yellow', 'green', 'purple', 'orange'];

const getColorForIndex = (index) => DOT_COLORS[index % DOT_COLORS.length];

const VenueCard = ({
  venueOwnerships,
  csrfToken,
  team,
  onAddVenue,
  onAddStage,
  onEditStage,
  onEdit,
  onDelete,
  onToggleActive
}) => {
  const [sortBy, setSortBy] = useState('venue');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusTab, setStatusTab] = useState('active');

  const inactiveCount = useMemo(() => {
    return venueOwnerships.filter((vo) => vo.active === false).length;
  }, [venueOwnerships]);

  const totalStages = useMemo(() => {
    return venueOwnerships.reduce((sum, vo) => sum + (vo.rooms ? vo.rooms.length : 0), 0);
  }, [venueOwnerships]);

  const filteredAndSortedVenues = useMemo(() => {
    let filtered = venueOwnerships.filter((vo) => (vo.active === false) === (statusTab === 'inactive'));

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((vo) => {
        const name = (vo.name || '').toLowerCase();
        const address = (vo.address1 || '').toLowerCase();
        const city = (vo.city || '').toLowerCase();
        const state = (vo.state_code_or_name || '').toLowerCase();
        const stageNames = (vo.rooms || [])
          .map((r) => (r.name || '').toLowerCase())
          .join(' ');
        return name.includes(term) || address.includes(term) || city.includes(term) || state.includes(term) || stageNames.includes(term);
      });
    }

    return [...filtered].sort((a, b) => {
      if (sortBy === 'venue') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortDirection === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }
      if (sortBy === 'stages') {
        const countA = a.rooms ? a.rooms.length : 0;
        const countB = b.rooms ? b.rooms.length : 0;
        return sortDirection === 'asc' ? countA - countB : countB - countA;
      }
      return 0;
    });
  }, [venueOwnerships, searchTerm, statusTab, sortBy, sortDirection]);

  const handleSortClick = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnKey);
      setSortDirection('asc');
    }
  };

  return (
    <div className="team-member-card">
      <div className="team-member-card-header">
        <div className="team-member-card-header-left">
          <div className="team-member-card-title-row">
            <h2 className="team-member-card-title">Venues</h2>
            <span className="team-member-count-badge">
              {totalStages} {totalStages === 1 ? 'stage' : 'stages'}
            </span>
          </div>
          <p className="team-member-card-subtitle">
            Manage the venues where your team collaborates.
          </p>
        </div>
        <button className="team-btn-primary" onClick={onAddVenue}>
          <PlusIcon width="16" height="16" />
          <span>Add a Venue</span>
        </button>
      </div>

      {venueOwnerships.length === 0 ? (
        <div className="team-empty-state">
          <div className="team-empty-state-icon">
            <BuildingIcon width="24" height="24" />
          </div>
          <div className="team-empty-state-title">No venues yet</div>
          <div className="team-empty-state-text">
            Add a venue to get started
          </div>
        </div>
      ) : (
        <>
        <div className="team-search-filters-row">
          <div className="team-search-filters-left">
            <TeamSearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search venues..."
            />
          </div>
          <div className="team-search-filters-right">
            <Tabs
              variant="button-border"
              collapseBelow={0}
              value={statusTab}
              onChange={setStatusTab}
              items={[
                { value: 'active', label: 'Active', count: venueOwnerships.length - inactiveCount },
                { value: 'inactive', label: 'Inactive', count: inactiveCount },
              ]}
            />
          </div>
        </div>
        {filteredAndSortedVenues.length === 0 && statusTab === 'inactive' && !searchTerm.trim() ? (
          <div className="py-5">
            <EmptyState
              size="sm"
              icon={Archive}
              title="No inactive venues"
              description="Mark a venue inactive when you no longer book shows there. It moves out of your Active list to keep things tidy, but nothing is deleted: its past events, orders, and reports stay put. Use Edit → Mark Inactive on any venue, and switch it back anytime."
            />
          </div>
        ) : filteredAndSortedVenues.length === 0 ? (
          <div className="team-empty-state">
            <div className="team-empty-state-icon">
              <SearchIcon width="24" height="24" />
            </div>
            <div className="team-empty-state-title">No venues found</div>
            <div className="team-empty-state-text">
              {searchTerm.trim() ? 'Try adjusting your search' : 'All of your venues are marked inactive'}
            </div>
          </div>
        ) : (
        <div className="team-table-wrapper team-table-wrapper--venue">
          <table className="team-table">
            <colgroup>
              <col style={{ width: '50%' }} />
              <col />
              <col style={{ width: '96px' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ paddingLeft: '24px' }}>
                  <button
                    type="button"
                    className="team-sort-header"
                    onClick={() => handleSortClick('venue')}
                  >
                    <span>Venue</span>
                    <SortIcon active={sortBy === 'venue'} direction={sortDirection} />
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="team-sort-header"
                    onClick={() => handleSortClick('stages')}
                  >
                    <span>Stages</span>
                    <SortIcon active={sortBy === 'stages'} direction={sortDirection} />
                  </button>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedVenues.map((vo, index) => {
                const rooms = vo.rooms || [];
                const address = [
                  vo.address1,
                  [vo.city, vo.state_code_or_name, vo.zip].filter(Boolean).join(', ')
                ].filter(Boolean).join(', ');

                return (
                  <tr key={vo.id}>
                    <td style={{ paddingLeft: '24px' }}>
                      <div className="team-member-info">
                        {vo.logo_thumb_url ? (
                          <div
                            className="team-avatar"
                            style={{
                              borderRadius: '8px',
                              overflow: 'hidden',
                              backgroundColor: 'var(--sl-bg-tertiary, #f5f5f5)'
                            }}
                          >
                            <img
                              src={vo.logo_thumb_url}
                              alt={vo.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </div>
                        ) : (
                          <div
                            className="team-avatar"
                            style={{
                              borderRadius: '8px',
                              backgroundColor: getVenueColor(vo.name)
                            }}
                            title={vo.name}
                          >
                            {getVenueInitials(vo.name)}
                          </div>
                        )}
                        <div className="team-member-details">
                          <span className="team-member-name">{vo.name}</span>
                          <span className="team-member-email">{address}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="team-venue-badges">
                        {[...rooms]
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                          .map((room, roomIndex) => (
                          <span
                            key={room.id}
                            className="team-venue-badge"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onEditStage(vo, room)}
                            title={`Edit ${room.name}`}
                          >
                            <span className={`team-venue-dot team-venue-dot-${getColorForIndex(roomIndex)}`}></span>
                            <span>{room.name}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <VenueActionsMenu
                        venueOwnership={vo}
                        onAddStage={onAddStage}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onToggleActive={onToggleActive}
                        csrfToken={csrfToken}
                        team={team}
                        openUpwards={index >= filteredAndSortedVenues.length - 2}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        </>
      )}
    </div>
  );
};

export default VenueCard;
